import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Database, Plus, RefreshCw } from "lucide-react";
import { useSettings } from "@/providers/SettingsProvider";
import { toast } from "@/hooks/use-toast";
import { generateDummyBelgianCompanies } from "@/utils/dummy-data/companies";
import { useTranslation } from "react-i18next";

interface CompaniesActionsProps {
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  fetchCompanies: () => Promise<void>;
  onAddCompany: () => void;
}

export const CompaniesActions = ({
  isGenerating,
  setIsGenerating,
  fetchCompanies,
  onAddCompany
}: CompaniesActionsProps) => {
  const { t } = useTranslation();
  // Use try-catch to handle the case where SettingsProvider is not available
  let devMode = false;
  try {
    const { devMode: settingsDevMode } = useSettings();
    devMode = settingsDevMode;
  } catch (error) {
    console.error("SettingsProvider not available:", error);
    // Continue with default value for devMode
  }

  const handleGenerateDummyData = async () => {
    try {
      setIsGenerating(true);
      await generateDummyBelgianCompanies();
      // Refresh companies list after generating data
      await fetchCompanies();
    } catch (error) {
      console.error("Failed to generate company data:", error);
      toast({
        title: t("candidates.actions.errorTitle"),
        description: t("candidates.actions.errorGenerate"),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex gap-2">
      {devMode && (
        <Button 
          variant="outline" 
          onClick={handleGenerateDummyData}
          disabled={isGenerating}
          className="flex items-center gap-2"
        >
          <Database className="h-4 w-4" />
          {isGenerating ? t("candidates.actions.generating") : t("candidates.actions.generateDummy")}
        </Button>
      )}
      <Button 
        onClick={onAddCompany} 
        className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90"
      >
        <Plus className="h-4 w-4 mr-2" />
        {t("companies.create.submit")}
      </Button>
    </div>
  );
};
